/**
 * CallLogsReportPage — V2 orchestrator for the Call Logs report.
 *
 * Brand-neutral name (currently Grandstream-backed, gated on is_grandstream
 * at the route + nav level). Mirrors AlertDetailsPage closely:
 *   - Time badge + popover (quick ranges; "Custom Range…" opens the drawer)
 *   - A "Report Settings" drawer holding Time Range + the Call Types filter
 *   - Summary ↔ Detailed view toggle
 * The backend returns only `records`; the summary is computed client-side.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
    getCampusConfig,
    getCampusTzOffset,
} from '../../../services/session.js';
import {
    iAmHigherAuthority,
    formatDateIso,
    formatDateTime12Hour,
    getToday,
} from '../../../libs.jsx';
import { fetchCallLogsReport, fetchCallTypes, fetchFilterOptions } from './api/callLogsReportApi.js';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';
import CallLogsSummary from './components/CallLogsSummary.jsx';
import CallLogsReportTable from './components/CallLogsReportTable.jsx';
import ReportFilterDrawer from '../alert-details/components/ReportFilterDrawer.jsx';
import { CALL_LOGS_REPORT_SECTIONS } from '../../../components/v2/reportFilterSections.js';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import useReportRestore from '../../../hooks/useReportRestore.js';
import { ReportRestoreOffer, ReportRestoreNote }
    from '../../../components/v2/ReportRestoreBanner.jsx';

import './call-logs-report-page.css';
import { REPORT_TYPE_ID } from '../../../utils/reportTypes.js';
import { countActiveFilters } from '../../../utils/reportFilterChips.js';
import { TIME_RANGES, initialRangeState, rangeStateFor, reportErrorMessage } from '../../../utils/reportPageState.js';


/* Cache scope. A literal, not the route param: rename a route and a
   param-derived key silently misses every cached report. */
const REPORT_TYPE = 'callLogs';

/* Both drawer groups start open, as the page's own drawer did before B4. */
const CALL_LOGS_EXPANDED = ['timeRange', 'callTypes'];


const CallLogsReportPage = () => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const tzOffset = getCampusTzOffset(campusId);
    const showCampusSelector = useMemo(
        () => iAmHigherAuthority(30, session?.role_weight),
        [session]
    );

    // -- Report restoration (history stamp + result cache + nav-menu offer) --
    /* Also supplies the navigation state this page used to read directly:
       ScheduledReportsPage "Run Report Local" pre-fills the time range and the
       call-type filter and auto-runs, and a stamped history entry does the same
       on browser back. */
    const restore = useReportRestore({ reportType: REPORT_TYPE, campusId });
    const { lookup: cacheLookup, commit: cacheCommit, endRefresh } = restore;
    const navState = restore.navState;

    // -- Report state --
    const [status, setStatus] = useState('init'); // 'init' | 'loading' | 'ready' | 'error'
    const [records, setRecords] = useState([]);
    const [errorMsg, setErrorMsg] = useState('');
    const [viewMode, setViewMode] = useState('summary'); // 'summary' | 'detailed'

    // -- Time range -- (seeded from nav state when launched via Run Report Local)
    const initRange = initialRangeState(navState);
    const [rangeId, setRangeId] = useState(initRange.rangeId);
    const [rangeName, setRangeName] = useState(initRange.rangeName);
    const [customRange, setCustomRange] = useState(initRange.customRange);
    const [start, setStart] = useState(() => {
        if (navState?.startDate) { const d = new Date(navState.startDate); if (!Number.isNaN(d.getTime())) return d; }
        const d = getToday(-1); d.setHours(0,0,0,0); return d;
    });
    const [end, setEnd] = useState(() => {
        if (navState?.endDate) { const d = new Date(navState.endDate); if (!Number.isNaN(d.getTime())) return d; }
        const d = getToday(-1); d.setHours(23,59,59,999); return d;
    });

    // -- Time popover --
    const [timePopoverOpen, setTimePopoverOpen] = useState(false);
    const timePopoverRef = useRef(null);

    // -- Settings drawer --
    const [settingsOpen, setSettingsOpen] = useState(false);

    // -- Schedule drawer --
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);
    const [filterOptions, setFilterOptions] = useState({}); // for the schedule user-recipient picker
    const [saving, setSaving] = useState(false);

    // -- Toast --
    const { toast, showToast: showTypedToast, dismissToast } = useToast(5000);
    /* This page's calls are (message, type); the shared hook is (type, message). */
    const showToast = useCallback((message, type = 'success') => showTypedToast(type, message), [showTypedToast]);

    // -- Call-type filter -- (seeded from nav state's callTypes when present)
    const [callTypes, setCallTypes] = useState([]);          // [{ call_type_id, call_type_name }]
    const [selectedCallTypes, setSelectedCallTypes] = useState(
        () => (navState?.filters?.callTypes || []).map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n))
    ); // number[]

    // Re-run after a settings apply/clear if the report was already run
    const shouldRerunRef = useRef(false);

    // Load call-type options once
    useEffect(() => {
        let cancelled = false;
        fetchCallTypes()
            .then((data) => { if (!cancelled) setCallTypes(Array.isArray(data) ? data : []); })
            .catch(() => { if (!cancelled) setCallTypes([]); });
        return () => { cancelled = true; };
    }, []);

    // Load frequencies + filter options (campus users for the recipient picker)
    // for the Schedule Report drawer.
    useEffect(() => {
        let cancelled = false;
        fetchFrequencies()
            .then((data) => { if (!cancelled) setFrequencies(Array.isArray(data) ? data : []); })
            .catch(() => { if (!cancelled) setFrequencies([]); });
        fetchFilterOptions(campusId)
            .then((opts) => { if (!cancelled) setFilterOptions(opts || {}); })
            .catch(() => { if (!cancelled) setFilterOptions({}); });
        return () => { cancelled = true; };
    }, [campusId]);

    // Merge call types into the schedule drawer's options under `callTypes` so
    // its filter-term builder can resolve call_type_id → name labels.
    const scheduleFilterOptions = useMemo(
        () => ({ ...filterOptions, callTypes }),
        [filterOptions, callTypes]
    );

    // Close time popover on outside click
    useEffect(() => {
        if (!timePopoverOpen) return;
        const onDocClick = (e) => {
            if (timePopoverRef.current && !timePopoverRef.current.contains(e.target)) setTimePopoverOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [timePopoverOpen]);

    // ----- Time handlers -----
    const handleTimeRange = useCallback((range) => {
        const [s, e] = range.getRange();
        setStart(s);
        setEnd(e);
        setRangeId(range.id);
        setRangeName(range.label);
        setCustomRange(false);
    }, []);

    const handleQuickTimeRange = useCallback((range) => {
        handleTimeRange(range);
        setTimePopoverOpen(false);
    }, [handleTimeRange]);

    const handleCustomToggle = useCallback(() => {
        setCustomRange(true);
        setRangeId(0);
        setRangeName('Custom');
    }, []);

    /** Custom Range… in the popover opens the Report Settings drawer. */
    const handlePopoverCustom = useCallback(() => {
        setTimePopoverOpen(false);
        handleCustomToggle();
        setSettingsOpen(true);
    }, [handleCustomToggle]);

    // ----- Run report -----
    /* `force` skips the cache. Every Run control passes it: a cache that
       ignored an explicit Run would be indistinguishable from a broken button. */
    const handleRunReport = useCallback(async ({ force = false } = {}) => {
        /* Key names match what the state initialisers read off navState, which
           is the hook's one contract -- the same object becomes both the cache
           key and the history stamp. */
        const runConfig = {
            rangeId,
            filters: { callTypes: selectedCallTypes },
            startDate: start.toISOString(),
            endDate: end.toISOString(),
        };
        const { hit, revalidating } = cacheLookup(runConfig, { force });

        /* A hit renders immediately, stale or not -- coming back should cost
           nothing. A stale one then re-queries underneath what is on screen. */
        if (hit) {
            setRecords(hit.records);
            setErrorMsg('');
            setStatus('ready');
            if (!revalidating) return;
        } else {
            setStatus('loading');
            setErrorMsg('');
        }

        const result = await fetchCallLogsReport({
            campusId,
            startDate: formatDateIso(start),
            endDate: formatDateIso(end),
            callTypeFilter: selectedCallTypes.join(','),
        });
        if (result?.ErrorCode) {
            /* A failed BACKGROUND refresh leaves the stale report up. Blanking a
               report someone is reading, because an edit elsewhere could not be
               re-fetched, is the wrong trade. */
            if (revalidating) {
                endRefresh();
                return;
            }
            setRecords([]);
            setStatus('error');
            setErrorMsg(reportErrorMessage(result.ErrorCode));
            return;
        }
        const rows = result.records || [];
        setRecords(rows);
        setStatus('ready');

        /* Caches the rows and stamps runConfig into the history entry, so
           browser back lands on something that re-seeds itself. */
        cacheCommit(runConfig, { records: rows, summary: null, overLimit: false, csvUrl: '' });
    }, [campusId, start, end, selectedCallTypes, rangeId,
        cacheLookup, cacheCommit, endRefresh]);

    // Re-run when a settings apply/clear changed the selection (only if already run)
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [selectedCallTypes, handleRunReport]);

    // Auto-run on mount when launched via "Run Report Local" from the
    // scheduled reports page (state.autoRun), or when returning to a stamped
    // history entry. Time range + call types are already seeded into state
    // above, so the initial run uses them. When the one-shot is not armed the
    // hook has already decided whether to offer the last run instead.
    useEffect(() => {
        if (restore.consumeAutoRun()) handleRunReport();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* Deferred one-shot. No dep array -- the ref gate is what makes it fire
       exactly once, on the render after the seeded state has settled. */
    const pendingRunRef = useRef(false);
    useEffect(() => {
        if (!pendingRunRef.current) return;
        pendingRunRef.current = false;
        handleRunReport();
    }); // runs every render; the ref gate makes it one-shot

    /* Accepting the nav-menu offer. Seeding is page state so it lives here;
       the hook only hands back the config it stored. */
    const handleAcceptRestore = useCallback(() => {
        const cfg = restore.acceptOffer();
        if (!cfg) return;
        const r = rangeStateFor(cfg.rangeId ?? 0);
        setRangeId(r.rangeId);
        setRangeName(r.rangeName);
        setCustomRange(r.customRange);
        if (cfg.startDate) setStart(new Date(cfg.startDate));
        if (cfg.endDate) setEnd(new Date(cfg.endDate));
        setSelectedCallTypes(
            (cfg.filters?.callTypes || []).map((id) => parseInt(id, 10))
                .filter((n) => !Number.isNaN(n))
        );
        pendingRunRef.current = true;
    }, [restore]);

    // ----- Settings drawer apply/clear -----
    /* The shared drawer works in { callTypes: string[] }; this page keeps number[]
       (call_type_id is an int). Memoised: the drawer resets its working copy whenever
       currentFilters changes identity. */
    const drawerFilters = useMemo(() => ({ callTypes: selectedCallTypes }), [selectedCallTypes]);
    const drawerOptions = useMemo(() => ({ callTypes }), [callTypes]);

    const handleApplySettings = useCallback((filters) => {
        const nextCallTypes = (filters.callTypes || [])
            .map((id) => parseInt(id, 10))
            .filter((n) => !Number.isNaN(n));
        setSelectedCallTypes(nextCallTypes);
        setSettingsOpen(false);
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    const handleClearSettings = useCallback(() => {
        setSelectedCallTypes([]);
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    // ----- Schedule report -----
    const handleScheduleSave = useCallback(async (payload) => {
        setSaving(true);
        const result = await createSchedule(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);
        if (result[0]) {
            showToast('Schedule created successfully.');
            setScheduleOpen(false);
        } else {
            showToast(result[1] || 'Failed to create schedule.', 'error');
        }
    }, [campusId, showToast]);

    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        setStatus('init');
        setRecords([]);
    }, []);

    /* Active filters, not selected items — the same badge on every report (audit A22). */
    const activeFilterCount = useMemo(() => countActiveFilters({ callTypes: selectedCallTypes }, CALL_LOGS_REPORT_SECTIONS), [selectedCallTypes]);

    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ps-page">

                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* PAGE HEADER */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <div className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ps-breadcrumb__current">Call Logs</span>
                                </div>

                                <div className="ps-campus-row">
                                    <label htmlFor="cl-campus-selector" className="ps-campus-label">Campus:</label>
                                    {showCampusSelector && campusList.length > 1 ? (
                                        <select id="cl-campus-selector" value={campusId} onChange={handleCampusChange} className="ps-campus-select">
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ps-page-title">Call Logs</h1>
                                </div>
                                <div className="ar-title-row__actions">
                                    {/* Time range badge + popover */}
                                    <div className="ar-time-badge-wrap" ref={timePopoverRef}>
                                        <span
                                            className={`ar-time-summary${timePopoverOpen ? ' ar-time-summary--open' : ''}`}
                                            onClick={() => setTimePopoverOpen((p) => !p)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTimePopoverOpen((p) => !p); } }}
                                            aria-expanded={timePopoverOpen}
                                            aria-haspopup="true"
                                        >
                                            <i className="fe fe-clock" style={{ marginRight: '4px', fontSize: 'var(--font-size-xs)' }} />
                                            {customRange
                                                ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                                : rangeName}
                                            <svg className="ar-time-summary__chevron" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        </span>

                                        {timePopoverOpen ? (
                                            <div className="ar-time-popover">
                                                <div className="ar-time-popover__grid">
                                                    {TIME_RANGES.map((range) => (
                                                        <button
                                                            key={range.id}
                                                            type="button"
                                                            className={`ar-time-popover__btn${rangeId === range.id && !customRange ? ' ar-time-popover__btn--active' : ''}`}
                                                            onClick={() => handleQuickTimeRange(range)}
                                                        >
                                                            {range.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="ar-time-popover__divider" />
                                                <button
                                                    type="button"
                                                    className={`ar-time-popover__custom${customRange ? ' ar-time-popover__custom--active' : ''}`}
                                                    onClick={handlePopoverCustom}
                                                >
                                                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                                    </svg>
                                                    Custom Range…
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* Schedule Report — static custom dates aren't eligible
                                        for recurring schedules, so it's disabled in custom mode. */}
                                    <span className="ar-disabled-tooltip-wrap">
                                        <button
                                            className="dp-btn dp-btn--secondary dp-btn--sm"
                                            onClick={() => setScheduleOpen(true)}
                                            disabled={customRange}
                                        >
                                            <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                        </button>
                                        {customRange ? (
                                            <span className="ar-disabled-tooltip" role="tooltip">
                                                Static custom dates are not eligible for recurring scheduled reporting.
                                            </span>
                                        ) : null}
                                    </span>

                                    {/* Report Settings (time + call types) */}
                                    <button
                                        className="dp-btn dp-btn--secondary dp-btn--sm"
                                        onClick={() => setSettingsOpen(true)}
                                        style={{ position: 'relative' }}
                                    >
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeFilterCount > 0 ? (
                                            <span className="ar-filter-badge">{activeFilterCount}</span>
                                        ) : null}
                                    </button>

                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={() => handleRunReport({ force: true })}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View toggle */}
                    {status === 'ready' ? (
                        <div className="ar-subtab-bar">
                            <div className="pp-subtab-toggle">
                                <button className={`pp-subtab-btn${viewMode === 'summary' ? ' is-active' : ''}`} onClick={() => setViewMode('summary')}>Summary</button>
                                <button className={`pp-subtab-btn${viewMode === 'detailed' ? ' is-active' : ''}`} onClick={() => setViewMode('detailed')}>Detailed</button>
                            </div>
                        </div>
                    ) : null}

                    {/* MAIN CONTENT */}
                    <div className="ar-content">
                        {/* A question, not a result: nothing has run in this
                            mount yet, so it sits above the report body. */}
                        <ReportRestoreOffer
                            offer={restore.offer}
                            onAccept={handleAcceptRestore}
                            onDismiss={restore.dismissOffer}
                            show={status === 'init'}
                        />
                        <ReportRestoreNote
                            servedFrom={restore.servedFrom}
                            refreshing={restore.refreshing}
                        />

                        {status === 'init' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-phone-call" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                                <p>Select a time range and click <strong>Run Report</strong> to generate the call logs report.</p>
                                <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={() => handleRunReport({ force: true })} style={{ marginTop: 'var(--space-3)' }}>
                                    <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />Run Report
                                </button>
                            </div>
                        ) : null}

                        {status === 'loading' ? (
                            <div className="ar-loading">
                                <div className="ar-loading__spinner" />
                                <p>Loading call logs...</p>
                            </div>
                        ) : null}

                        {status === 'error' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-alert-triangle" style={{ fontSize: '40px', color: 'var(--color-warning)', marginBottom: 'var(--space-4)' }} />
                                <p>{errorMsg}</p>
                                <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={() => handleRunReport({ force: true })} style={{ marginTop: 'var(--space-3)' }}>
                                    <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />Try Again
                                </button>
                            </div>
                        ) : null}

                        {status === 'ready' ? (
                            viewMode === 'summary'
                                ? <CallLogsSummary records={records} />
                                : <CallLogsReportTable
                                    records={records}
                                    tzOffset={tzOffset}
                                    dateRangeLabel={customRange
                                        ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                        : rangeName}
                                  />
                        ) : null}
                    </div>

                    {/* Report Settings drawer */}
                    <ReportFilterDrawer
                        sections={CALL_LOGS_REPORT_SECTIONS}
                        hideResponseTime
                        dateOnly
                        defaultExpanded={CALL_LOGS_EXPANDED}
                        open={settingsOpen}
                        onClose={() => setSettingsOpen(false)}
                        onApply={handleApplySettings}
                        onClear={handleClearSettings}
                        saving={status === 'loading'}
                        filterOptions={drawerOptions}
                        currentFilters={drawerFilters}
                        timeRanges={TIME_RANGES}
                        rangeId={rangeId}
                        customRange={customRange}
                        start={start}
                        end={end}
                        onTimeRangeChange={handleTimeRange}
                        onCustomToggle={handleCustomToggle}
                        onStartChange={(date) => setStart(date)}
                        onEndChange={(date) => setEnd(date)}
                    />

                    {/* Schedule Report drawer (report_type_id 14) */}
                    <ScheduleReportDrawer
                        open={scheduleOpen}
                        onClose={() => setScheduleOpen(false)}
                        onSave={handleScheduleSave}
                        saving={saving}
                        reportTypeId={REPORT_TYPE_ID.CALL_LOGS}
                        rangeId={rangeId}
                        rangeName={customRange ? 'Custom' : rangeName}
                        reportFilters={{ callTypes: selectedCallTypes }}
                        filterOptions={scheduleFilterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />
                </div>
            </div>
        </>
    );
};

export default CallLogsReportPage;
